import { useApolloClient } from "@apollo/client";
import { useAuth0 } from "@auth0/auth0-react";
import {
  AppBar,
  createStyles,
  ListSubheader,
  Theme,
  Toolbar,
  MenuList,
  MenuItem,
  Drawer,
  List,
  IconButton,
} from "@material-ui/core";
import { blueGrey } from "@material-ui/core/colors";
import { makeStyles } from "@material-ui/core/styles";
import cx from "classnames";
import {
  DotsVertical,
  ArchiveOutline,
  BriefcaseOutline,
  ViewGridOutline,
  AccountMultiple,
} from "mdi-material-ui";
import Menu from "mdi-material-ui/Menu";
import React, { useState } from "react";
import { useLocation, useHistory, Route } from "react-router-dom";

import PopoverIconButton from "../../../components/PopoverIconButton";
import useMobile from "../../../hooks/useMobile";
import AkteNavigationList from "../../akte/containers/AkteNavigationList";
import { logout } from "../../core/api";
import logo from "../assets/logo-osv-white.png";
import runtimeConfig from "../../core/runtimeConfig";

import CollapsableMenuItem from "./CollapsableMenuItem";
import OSFooter from "./OSFooter";
import LVFooter from "./LFFooter";

const drawerWidth = 240;

const useStyles = makeStyles<Theme>(
  ({ mixins, spacing, transitions, zIndex }) =>
    createStyles({
      appBar: {
        zIndex: zIndex.drawer + 1,
      },
      root: {
        height: "100%",
        display: "flex",
        backgroundColor: blueGrey[50],
        overflow: "auto",
        paddingTop: spacing(2),
      },
      menuButton: {
        margin: spacing(0, 1.5, 0, -2),
      },
      drawer: {
        width: drawerWidth,
        flexShrink: 0,
      },
      drawerPaper: {
        width: drawerWidth,
      },
      content: {
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        padding: spacing(0, 2),
        "&:not($contentMobile)": {
          transition: transitions.create("margin", {
            easing: transitions.easing.sharp,
            duration: transitions.duration.leavingScreen,
          }),
          marginLeft: -drawerWidth,
        },
      },
      contentShift: {
        "&:not($contentMobile)": {
          transition: transitions.create("margin", {
            easing: transitions.easing.easeOut,
            duration: transitions.duration.enteringScreen,
          }),
          marginLeft: 0,
        },
      },
      contentMobile: {},
      spacer: {
        marginTop: "auto",
      },
      toolbar: mixins.toolbar,
      profileButton: {
        marginLeft: spacing(1),
        position: "absolute",
        right: spacing(1),
      },
    })
);

interface Props {
  children?: React.ReactNode;
  testId?: string;
}

const DashboardPage: React.FunctionComponent<Props> = ({
  children,
  testId,
}) => {
  const classes = useStyles();
  const { isMobile } = useMobile();
  const isOSInstance = runtimeConfig.partnerAuth === "OS";

  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const location = useLocation();
  const history = useHistory();
  const apollo = useApolloClient();
  const auth0 = useAuth0();

  const handleLogout = () => {
    logout(apollo, auth0);
    history.push("/auth/login?loggedout");
  };

  const searchParameter = new URLSearchParams(location.search);

  return (
    <div className={classes.root} data-testid={`DashboardPage-${testId}`}>
      <AppBar position="fixed" className={classes.appBar}>
        <Toolbar variant={isMobile ? "regular" : "dense"}>
          <IconButton
            color={"inherit"}
            onClick={() => setDrawerOpen((closed) => !closed)}
            className={classes.menuButton}
          >
            <Menu />
          </IconButton>
          {isOSInstance && (
            <img alt={"otto-schmidt Logo"} src={logo} height={24} />
          )}
          <PopoverIconButton
            tooltip={"Account"}
            className={classes.profileButton}
            color="inherit"
            icon={DotsVertical}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <MenuList>
              <MenuItem onClick={handleLogout}>Abmelden</MenuItem>
            </MenuList>
          </PopoverIconButton>
        </Toolbar>
      </AppBar>
      <Drawer
        open={drawerOpen}
        className={classes.drawer}
        variant={isMobile ? "temporary" : "persistent"}
        onClose={() => setDrawerOpen(false)}
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        {!isMobile && <div className={classes.toolbar} />}
        <CollapsableMenuItem
          label={"Startseite"}
          icon={<ViewGridOutline />}
          key={"home"}
          onClick={() => {
            history.push("/dashboard");
          }}
          selected={location.pathname.toLowerCase().endsWith("/dashboard")}
        />
        <CollapsableMenuItem
          label={"Personenverzeichnis"}
          icon={<AccountMultiple />}
          key={"people"}
          onClick={() => {
            history.push("/dashboard/personen");
          }}
          selected={location.pathname
            .toLowerCase()
            .endsWith("/dashboard/personen")}
        />
        <List
          dense={!isMobile}
          component="nav"
          aria-labelledby="nested-list-subheader"
          subheader={
            <ListSubheader component="div" id="nested-list-subheader">
              Meine Akten
            </ListSubheader>
          }
        >
          <CollapsableMenuItem
            label={"Akten"}
            icon={<BriefcaseOutline />}
            key={"akten"}
            onClick={() => {
              history.push("/dashboard/akten?status=current");
            }}
            selected={
              location.pathname.toLowerCase().endsWith("/dashboard/akten") &&
              (searchParameter.get("status") || "current") === "current"
            }
          />
          <CollapsableMenuItem
            label={"Archiv"}
            icon={<ArchiveOutline />}
            key={"archivedAkten"}
            onClick={() => {
              history.push("/dashboard/akten?status=archived");
            }}
            selected={
              location.pathname.toLowerCase().endsWith("/dashboard/akten") &&
              searchParameter.get("status") === "archived"
            }
          />
        </List>
        <Route
          path="/dashboard/akten/:id/fokos/:fokoid"
          component={AkteNavigationList}
        />
      </Drawer>
      <main
        className={cx(classes.content, {
          [classes.contentShift]: drawerOpen,
          [classes.contentMobile]: isMobile,
        })}
      >
        <div className={classes.toolbar} />
        {children}
        <div className={classes.spacer} />
        {isOSInstance ? <OSFooter /> : <LVFooter />}
      </main>
    </div>
  );
};

export default DashboardPage;
